import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Shared Simulation Settings Component
 * Used by both Screener and Watchlist modules
 * Allows users to adjust Days to Exit and Underlying Gain % parameters
 * and recalculate option values client-side.
 * Auto-updates the parent table on slider changes (debounced).
 */
const SimulationSettingsPanel = ({
  maxDays = 365,
  maxExpirationDate = null,
  onRefresh,
  onReset,
  isActive = false,
  defaultDays = null,
  defaultGain = null,
  contractCount = 0,
  label = 'All',
  defaultExpanded = false,
  externalDays = null, // Sync slider when days change externally (e.g., from heatmap view)
  // Optional: Days to Exit mode toggle
  daysToExitMode = null, // 'per-contract' | 'fixed' | 'percent' | null (no toggle)
  daysToExitPercent = null, // 0-1 float
  onModeChange = null,
  onPercentChange = null,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [daysToTarget, setDaysToTarget] = useState(defaultDays || Math.min(45, maxDays));
  const [underlyingGain, setUnderlyingGain] = useState(defaultGain || 10);

  // Hot button values for quick selection
  const hotButtons = [-50, -35, -20, -10, -5, 0, 5, 10, 15, 20, 35, 50, 100, 200, 1000];

  // Whether the mode toggle is available
  const hasModeToggle = onModeChange && onPercentChange;
  const isPercentMode = daysToExitMode === 'percent';

  // Debounced auto-refresh: fires onRefresh automatically when sliders change
  const debounceRef = useRef(null);
  const autoRefresh = useCallback((days, gain) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (onRefresh) onRefresh(days, gain);
    }, 80);
  }, [onRefresh]);

  // Auto-refresh when days or gain changes (the core auto-update behavior)
  useEffect(() => {
    // Only auto-refresh if the panel has been interacted with (not on initial mount defaults)
    if (isActive || daysToTarget !== (defaultDays || Math.min(45, maxDays)) || underlyingGain !== (defaultGain || 10)) {
      autoRefresh(daysToTarget, underlyingGain);
    }
  }, [daysToTarget, underlyingGain]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync slider when days change externally (e.g., from heatmap view)
  useEffect(() => {
    if (externalDays !== null && externalDays !== daysToTarget) {
      setDaysToTarget(externalDays);
    }
  }, [externalDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update days when maxDays changes
  useEffect(() => {
    if (daysToTarget > maxDays) {
      setDaysToTarget(maxDays);
    }
  }, [maxDays, daysToTarget]);

  // Track if values have changed from defaults
  const hasChanges = (() => {
    const daysChanged = defaultDays !== null && daysToTarget !== (defaultDays || Math.min(45, maxDays));
    const gainChanged = defaultGain !== null && underlyingGain !== (defaultGain || 10);
    const modeChanged = isPercentMode;
    return daysChanged || gainChanged || modeChanged;
  })();

  const handleReset = () => {
    setDaysToTarget(defaultDays || Math.min(45, maxDays));
    setUnderlyingGain(defaultGain || 10);
    if (onReset) {
      onReset();
    }
  };

  const handleHotButton = (value) => {
    setUnderlyingGain(value);
  };

  const formatGainLabel = (value) => {
    if (value === 0) return '0%';
    return value > 0 ? `+${value}%` : `${value}%`;
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  // Calculate exit date based on days to exit
  const getTargetDate = (days) => {
    const today = new Date();
    const targetDate = new Date(today.getTime() + (days * 24 * 60 * 60 * 1000));
    return targetDate;
  };

  // Format date as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date display with days
  const formatDateWithDays = (date, days) => {
    return `${formatDate(date)} (${days} d.)`;
  };

  // Get formatted target date display
  const getFormattedTargetDate = () => {
    const targetDate = getTargetDate(daysToTarget);
    return formatDateWithDays(targetDate, daysToTarget);
  };

  // Get formatted max expiration date display
  const getFormattedMaxDate = () => {
    if (maxExpirationDate) {
      const date = new Date(maxExpirationDate);
      return formatDateWithDays(date, maxDays);
    }
    // Fallback to calculating from days if date not provided
    const maxDate = getTargetDate(maxDays);
    return formatDateWithDays(maxDate, maxDays);
  };

  return (
    <div className="simulation-settings bg-gray-800 rounded-lg mb-4">
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-750 transition-colors"
        onClick={toggleExpanded}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-200">
            Simulation Settings
          </h3>
          <span className="text-xs text-gray-400">
            {isActive ? (
              <span className="text-blue-500">
                Active ({isPercentMode
                  ? `${Math.round((daysToExitPercent || 0.30) * 100)}% of exp`
                  : `${daysToTarget} days`
                }, {formatGainLabel(underlyingGain)})
              </span>
            ) : (
              `${label} (${contractCount} contracts)`
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {!isExpanded && isActive && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleReset();
              }}
              className="px-3 py-1 text-xs bg-gray-700 text-gray-300 rounded hover:bg-gray-600 transition-colors"
            >
              Reset
            </button>
          )}
          <span className={`text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
            ›
          </span>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* Days to Exit Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-gray-300">
                  Days to Exit
                </label>
                {/* Mode Toggle (only when screener provides the props) */}
                {hasModeToggle && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        onModeChange('fixed');
                      }}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        !isPercentMode
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      Fixed Days
                    </button>
                    <button
                      onClick={() => onPercentChange(daysToExitPercent || 0.30)}
                      className={`px-2 py-0.5 text-[10px] rounded transition-colors ${
                        isPercentMode
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                      }`}
                    >
                      % of Exp
                    </button>
                  </div>
                )}
              </div>
              <span className="text-sm font-bold text-blue-500">
                {isPercentMode
                  ? `${Math.round((daysToExitPercent || 0.30) * 100)}% of expiration`
                  : getFormattedTargetDate()
                }
              </span>
            </div>

            {isPercentMode ? (
              /* Percentage slider: 5% to 100% */
              <div className="relative">
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={Math.round((daysToExitPercent || 0.30) * 100)}
                  onChange={(e) => onPercentChange(Number(e.target.value) / 100)}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #218aff 0%, #218aff ${
                      ((Math.round((daysToExitPercent || 0.30) * 100) - 5) / 95) * 100
                    }%, #374151 ${((Math.round((daysToExitPercent || 0.30) * 100) - 5) / 95) * 100}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>5%</span>
                  <span>50%</span>
                  <span>100%</span>
                </div>
              </div>
            ) : (
              /* Fixed days slider (existing) */
              <div className="relative">
                <input
                  type="range"
                  min="1"
                  max={maxDays}
                  value={daysToTarget}
                  onChange={(e) => setDaysToTarget(Number(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #218aff 0%, #218aff ${
                      (daysToTarget / maxDays) * 100
                    }%, #374151 ${(daysToTarget / maxDays) * 100}%, #374151 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 d.</span>
                  <span>{Math.floor(maxDays / 2)} d.</span>
                  <span>{getFormattedMaxDate()}</span>
                </div>
              </div>
            )}
          </div>

          {/* Underlying Gain Slider */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-gray-300">
                Underlying Gain
              </label>
              <span className="text-sm font-bold text-blue-500">
                {formatGainLabel(underlyingGain)}
              </span>
            </div>
            <div className="relative">
              <input
                type="range"
                min="-90"
                max="200"
                value={Math.min(underlyingGain, 200)}
                onChange={(e) => setUnderlyingGain(Number(e.target.value))}
                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #218aff 0%, #218aff ${
                    underlyingGain > 200
                      ? 100  // If value exceeds slider max, show fully filled
                      : ((Math.min(underlyingGain, 200) + 90) / 290) * 100
                  }%, #374151 ${
                    underlyingGain > 200
                      ? 100  // If value exceeds slider max, show fully filled
                      : ((Math.min(underlyingGain, 200) + 90) / 290) * 100
                  }%, #374151 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>-90%</span>
                <span>+55%</span>
                <span>+200%</span>
              </div>
            </div>

            {/* Hot Buttons */}
            <div className="flex flex-wrap gap-1 mt-3">
              {hotButtons.map((value) => (
                <button
                  key={value}
                  onClick={() => handleHotButton(value)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    underlyingGain === value
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {formatGainLabel(value)}
                </button>
              ))}
            </div>
          </div>

          {/* Reset Button */}
          {(isActive || hasChanges) && (
            <div className="pt-2">
              <button
                onClick={handleReset}
                className="w-1/3 px-4 py-2 text-sm font-medium rounded transition-colors bg-gray-700 text-gray-300 hover:bg-gray-600"
              >
                Reset
              </button>
            </div>
          )}
        </div>
      )}

      {/* Custom Slider Styles */}
      <style jsx>{`
        input[type="range"]::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          background: #218aff;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1f2937;
        }

        input[type="range"]::-moz-range-thumb {
          width: 16px;
          height: 16px;
          background: #218aff;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1f2937;
        }

        input[type="range"]:focus {
          outline: none;
        }

        input[type="range"]:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
        }
      `}</style>
    </div>
  );
};

export default SimulationSettingsPanel;
